import { useEffect, useState } from "react";
import Header from "@/components/site/Header";
import Ticker from "@/components/site/Ticker";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import AdSlot from "@/components/site/AdSlot";
import { api, fmt } from "@/lib/api";

const SOURCES = [
  { id: "reddit", label: "Reddit", sub: "r/popular · top today" },
  { id: "hackernews", label: "Hacker News", sub: "front page · live points" },
  { id: "github", label: "GitHub", sub: "hottest new repos · 7 days" },
];

export default function SignalsPage() {
  const [source, setSource] = useState("reddit");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/signals?source=${source}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
  }, [source]);

  const active = SOURCES.find((s) => s.id === source);

  return (
    <div data-testid="signals-page" className="bg-white text-[#0a0a0a]">
      <Seo
        title="Signals — what Reddit, Hacker News and GitHub are obsessing over right now"
        description="A live radar of the internet's other trend engines: Reddit's front page, Hacker News points, and GitHub's hottest new repositories. Updated hourly."
      />
      <Header />
      <Ticker />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="overline text-[#52525b] mb-4">New · Multi-source radar</div>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl lg:text-7xl">
            Signals.<br />
            <span className="text-[#ff3b30]">The internet&apos;s other pulse.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[#52525b] text-base lg:text-lg">
            Google tells you what people search. Signals tells you what they upvote, discuss and build —
            live from Reddit, Hacker News and GitHub. Refreshed every hour.
          </p>

          <div className="mt-10 flex flex-wrap gap-0 border border-black w-fit">
            {SOURCES.map((s) => (
              <button
                key={s.id}
                data-testid={`signals-tab-${s.id}`}
                onClick={() => setSource(s.id)}
                className={`px-6 py-3 overline border-r border-black last:border-r-0 transition-colors ${
                  source === s.id ? "bg-black text-white" : "bg-white hover:bg-[#f4f4f5]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="mt-2 font-mono text-xs text-[#52525b]">
            {active?.sub}
            {data?.fetched_at && ` · fetched ${data.fetched_at.replace("T", " ").slice(0, 16)} UTC`}
            {data?.stale && " · showing cached data"}
          </div>
        </div>
      </section>

      <AdSlot slot={process.env.REACT_APP_ADSENSE_SLOT_MID} label="AD · SIGNALS" testId="ad-signals" />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          {loading ? (
            <div data-testid="signals-loading" className="font-mono text-xs text-[#52525b] py-20 text-center uppercase tracking-widest">
              Scanning {active?.label}…
            </div>
          ) : !data?.items?.length ? (
            <div data-testid="signals-empty" className="font-mono text-xs text-[#52525b] py-20 text-center">
              No signals right now — the source may be rate-limiting. Try again in a minute.
            </div>
          ) : (
            <ol className="divide-y divide-black/15 border border-black">
              {data.items.map((item, i) => (
                <li key={item.id}>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`signal-item-${i}`}
                    className="flex items-baseline gap-4 lg:gap-6 p-4 lg:p-5 hover:bg-[#fafafa] transition-colors group"
                  >
                    <span className="font-mono text-xs text-[#52525b] w-7 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="font-display font-semibold tracking-tight text-base lg:text-lg group-hover:text-[#ff3b30] transition-colors">
                        {item.title}
                      </span>
                      <span className="block font-mono text-[11px] text-[#52525b] mt-1">
                        {item.tag} · {item.secondary}
                      </span>
                    </span>
                    <span className="font-mono text-sm whitespace-nowrap shrink-0 text-right">
                      {source === "reddit" ? `#${item.metric}` : fmt(item.metric)}
                      <span className="block text-[10px] text-[#52525b]">{item.metric_label}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
